import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Pilcrow, Heading, Undo, Redo, Palette, Eraser } from "lucide-react";

/**
 * Lightweight rich-text editor (contentEditable + execCommand).
 *
 * Features:
 *  - Bold, Italic, Underline
 *  - Paragraph / H3
 *  - Bulleted / numbered lists
 *  - Text color picker (presets + custom)
 *  - Clear formatting
 *  - Undo / Redo
 *  - Smart paste: preserves bold, italic, underline, lists, colors, paragraphs.
 *    Strips: scripts, classes, ids, images, links, unsafe fonts and sizes,
 *    background colors, margins, alignment, and any other unsafe inline styles.
 *
 * Stores HTML. Props: value, onChange, placeholder, minHeight, testId.
 */
const FONTS = ["Roboto", "OpenSans", "Lato", "Montserrat"];
const SIZES = [12, 14, 16, 18, 20, 24, 28, 32];

const RichText = forwardRef(function RichText({ value = "", onChange, placeholder = "Scrivi qui...", minHeight = 140, testId, allowTypography = false, viewportHeight = false }, editorRef) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const applyingTypography = useRef(false);
  const lastEmitted = useRef(null);
  const [focused, setFocused] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [fontChoice, setFontChoice] = useState("");
  const [sizeChoice, setSizeChoice] = useState("");
  const [lastColor, setLastColor] = useState(() => localStorage.getItem("cosm_rt_color") || "#1A1A1A");

  // Sync external value into the DOM only when it actually differs.
  useEffect(() => {
    if (!ref.current) return;
    // Local edits already live in contentEditable. Replacing its HTML with a
    // stale prop would erase the text/caret while React processes onChange.
    if (value === lastEmitted.current) return;
    if (ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  // Close color popover on outside click.
  useEffect(() => {
    if (!colorOpen) return;
    const close = (e) => {
      const wrap = ref.current?.closest("[data-rt-wrap]");
      if (!wrap?.contains(e.target)) setColorOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [colorOpen]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    handleInput();
  };
  const applyColor = (color) => {
    setLastColor(color);
    localStorage.setItem("cosm_rt_color", color);
    exec("foreColor", color);
    setColorOpen(false);
  };
  const clearFormatting = () => exec("removeFormat");

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && ref.current?.contains(selection.anchorNode)) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
      if (allowTypography) {
        let node = selection.anchorNode.nodeType === Node.TEXT_NODE
          ? selection.anchorNode.parentElement : selection.anchorNode;
        let font = "";
        let size = "";
        while (node && node !== ref.current) {
          if (!font && FONTS.includes(node.style?.fontFamily?.replace(/["']/g, ""))) {
            font = node.style.fontFamily.replace(/["']/g, "");
          }
          if (!size && SIZES.some((value) => node.style?.fontSize === `${value}px`)) {
            size = node.style.fontSize;
          }
          node = node.parentElement;
        }
        setFontChoice(font);
        setSizeChoice(size);
      }
    }
  };
  const styleSelection = (command, choice) => {
    const editor = ref.current;
    if (!editor || !choice) return;
    const range = savedRange.current;
    if (range && editor.contains(range.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
    const selectedOffsets = selectionOffsets(editor);
    editor.focus();
    applyingTypography.current = true;
    try {
      // execCommand fires input synchronously in Chromium. Normalizing during
      // that event would drop its temporary size="7" before we can map it.
      document.execCommand(command, false, command === "fontSize" ? "7" : choice);
      normalizeFontTags(editor, command === "fontSize" ? choice : null);
    } finally {
      applyingTypography.current = false;
    }
    if (selectedOffsets) restoreSelection(editor, selectedOffsets);
    rememberSelection();
    handleInput();
  };

  const handleInput = () => {
    if (applyingTypography.current) return;
    if (allowTypography && ref.current) normalizeFontTags(ref.current);
    const html = ref.current?.innerHTML || "";
    lastEmitted.current = html;
    onChange?.(html);
  };

  useImperativeHandle(editorRef, () => ({
    insertText(text) {
      const editor = ref.current;
      if (!editor) return;
      editor.focus();
      const selection = window.getSelection();
      const range = selection?.rangeCount && editor.contains(selection.anchorNode)
        ? selection.getRangeAt(0) : document.createRange();
      if (!selection?.rangeCount || !editor.contains(selection.anchorNode)) {
        range.selectNodeContents(editor);
        range.collapse(false);
      }
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      handleInput();
    },
  }));

  // Smart paste — preserve safe formatting, drop everything else.
  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) {
      const clean = sanitizePastedHtml(html);
      document.execCommand("insertHTML", false, clean);
    } else if (text) {
      // Preserve line breaks for plain-text paste.
      const safe = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\r?\n/g, "<br>");
      document.execCommand("insertHTML", false, safe);
    }
  };

  const isEmpty = !value || value === "<br>" || value === "<div><br></div>" || value.replace(/<[^>]+>/g, "").trim() === "";

  return (
    <div data-rt-wrap
         className={`border rounded-sm bg-white transition ${viewportHeight ? "flex min-h-[320px] flex-col" : ""} ${focused ? "border-[#1A1A1A]" : "border-[#E5E5E0]"}`}
         style={viewportHeight ? { height: "clamp(320px, calc(100dvh - 7rem), 1050px)" } : undefined}
         data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div className="shrink-0 flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#E5E5E0] bg-[#FAF9F6]" data-testid={testId ? `${testId}-toolbar` : undefined}>
        <TBtn onClick={() => exec("bold")} title="Grassetto (Ctrl+B)" testId={testId ? `${testId}-bold` : undefined}><Bold className="w-3.5 h-3.5" strokeWidth={2}/></TBtn>
        <TBtn onClick={() => exec("italic")} title="Corsivo (Ctrl+I)" testId={testId ? `${testId}-italic` : undefined}><Italic className="w-3.5 h-3.5" strokeWidth={2}/></TBtn>
        <TBtn onClick={() => exec("underline")} title="Sottolineato (Ctrl+U)" testId={testId ? `${testId}-underline` : undefined}><Underline className="w-3.5 h-3.5" strokeWidth={2}/></TBtn>
        <Sep/>
        <ColorPickerButton
          open={colorOpen}
          setOpen={setColorOpen}
          lastColor={lastColor}
          onPick={applyColor}
          testId={testId}
        />
        <TBtn onClick={clearFormatting} title="Rimuovi formattazione" testId={testId ? `${testId}-clear` : undefined}><Eraser className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        {allowTypography && <>
          <Sep/>
          <select aria-label="Carattere" value={fontChoice} onPointerDownCapture={rememberSelection}
            onChange={(event) => styleSelection("fontName", event.target.value)}
            className="h-8 max-w-[130px] min-w-0 border border-[#858585] bg-white px-1.5 text-xs text-[#1A1A1A] focus-visible:outline-[#000000]"
            data-testid={testId ? `${testId}-font-family` : undefined}>
            <option value="" disabled data-testid={testId ? `${testId}-font-placeholder` : undefined}>Carattere</option>
            {FONTS.map((font) => <option key={font} value={font}
              data-testid={testId ? `${testId}-font-${font === "OpenSans" ? "open-sans" : font.toLowerCase()}` : undefined}>
              {font === "OpenSans" ? "Open Sans" : font}</option>)}
          </select>
          <select aria-label="Dimensione testo" value={sizeChoice} onPointerDownCapture={rememberSelection}
            onChange={(event) => styleSelection("fontSize", event.target.value)}
            className="h-8 w-[87px] border border-[#858585] bg-white px-1.5 text-xs text-[#1A1A1A] focus-visible:outline-[#000000]"
            data-testid={testId ? `${testId}-font-size` : undefined}>
            <option value="" disabled data-testid={testId ? `${testId}-size-placeholder` : undefined}>Misura</option>
            {SIZES.map((size) => <option key={size} value={`${size}px`}
              data-testid={testId ? `${testId}-size-${size}` : undefined}>{size} px</option>)}
          </select>
        </>}
        <Sep/>
        <TBtn onClick={() => exec("formatBlock", "P")} title="Paragrafo" testId={testId ? `${testId}-paragraph` : undefined}><Pilcrow className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        <TBtn onClick={() => exec("formatBlock", "H3")} title="Titolo" testId={testId ? `${testId}-heading` : undefined}><Heading className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        <Sep/>
        <TBtn onClick={() => exec("insertUnorderedList")} title="Elenco puntato" testId={testId ? `${testId}-ul` : undefined}><List className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        <TBtn onClick={() => exec("insertOrderedList")} title="Elenco numerato" testId={testId ? `${testId}-ol` : undefined}><ListOrdered className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        <Sep/>
        <TBtn onClick={() => exec("undo")} title="Annulla" testId={testId ? `${testId}-undo` : undefined}><Undo className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
        <TBtn onClick={() => exec("redo")} title="Ripristina" testId={testId ? `${testId}-redo` : undefined}><Redo className="w-3.5 h-3.5" strokeWidth={1.5}/></TBtn>
      </div>
      <div className={`relative ${viewportHeight ? "flex-1 min-h-0 overflow-y-auto" : ""}`} data-testid={testId ? `${testId}-scroll-region` : undefined}>
        {isEmpty && !focused && (
          <div className="workflow-ux-richtext-placeholder absolute top-3 left-4 text-sm text-[#A09988] pointer-events-none"
               data-testid={testId ? `${testId}-placeholder` : undefined}>{placeholder}</div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onMouseUp={rememberSelection}
          onKeyUp={rememberSelection}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => { rememberSelection(); setFocused(false); }}
          className="px-4 py-3 text-sm leading-relaxed outline-none prose-rt"
          style={viewportHeight ? { minHeight: "100%" } : { minHeight: `${minHeight}px` }}
          data-testid={testId}
        />
      </div>
    </div>
  );
});

export default RichText;

// Text offsets survive execCommand's replacement of <font> nodes with spans.
// Restoring the DOM Range itself would leave the highlight on detached nodes.
function selectionOffsets(editor) {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editor.contains(selection.anchorNode) ||
      !editor.contains(selection.focusNode)) return null;
  const range = selection.getRangeAt(0);
  const before = document.createRange();
  before.selectNodeContents(editor);
  before.setEnd(range.startContainer, range.startOffset);
  return { start: before.toString().length, end: before.toString().length + range.toString().length };
}

function restoreSelection(editor, offsets) {
  const nodes = [];
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) nodes.push(walker.currentNode);
  if (!nodes.length) return;
  const locate = (target, forward) => {
    let position = 0;
    for (let i = 0; i < nodes.length; i += 1) {
      const length = nodes[i].textContent.length;
      if (target < position + length || (target === position + length && (!forward || i === nodes.length - 1))) {
        return [nodes[i], Math.max(0, Math.min(length, target - position))];
      }
      position += length;
    }
    return [nodes[nodes.length - 1], nodes[nodes.length - 1].textContent.length];
  };
  const [startNode, startOffset] = locate(offsets.start, true);
  const [endNode, endOffset] = locate(offsets.end, false);
  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function normalizeFontTags(editor, selectedSize = null) {
  editor.querySelectorAll("font").forEach((font) => {
    const span = document.createElement("span");
    const face = font.getAttribute("face")?.trim();
    if (FONTS.includes(face)) span.style.fontFamily = face;
    const fontSize = selectedSize || font.style.fontSize;
    if (SIZES.some((size) => `${size}px` === fontSize)) span.style.fontSize = fontSize;
    const color = font.getAttribute("color");
    if (/^#[0-9a-f]{6}$/i.test(color || "")) span.style.color = color;
    while (font.firstChild) span.appendChild(font.firstChild);
    font.replaceWith(span);
  });
}

const PRESETS = [
  "#1A1A1A", "#707070", "#FFFFFF",
  "#8C3A3A", "#C8264F", "#D4A373",
  "#4A6B53", "#3B6A8C", "#7A5BA8",
];

function ColorPickerButton({ open, setOpen, lastColor, onPick, testId }) {
  return (
    <div className="relative">
      <button type="button"
              onMouseDown={(e)=>e.preventDefault()}
              onClick={() => setOpen(o => !o)}
              title="Colore testo"
              data-testid={testId ? `${testId}-color` : undefined}
              className="p-1.5 rounded-sm hover:bg-[#E5E5E0] text-[#1A1A1A] flex items-center gap-1">
        <Palette className="w-3.5 h-3.5" strokeWidth={1.5}/>
        <span className="w-3.5 h-1 rounded-sm" style={{ background: lastColor }} aria-hidden="true"/>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-[#E5E5E0] shadow-lg p-3 w-48 rounded-sm"
             data-testid={testId ? `${testId}-color-popover` : undefined}
             onMouseDown={(e)=>e.preventDefault()}>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {PRESETS.map(c => (
              <button key={c} type="button" onClick={() => onPick(c)}
                       className="h-6 rounded-sm border border-[#E5E5E0] hover:scale-110 transition"
                       style={{ background: c }}
                       title={c}
                       data-testid={testId ? `${testId}-color-${c.replace("#","")}` : undefined} />
            ))}
          </div>
          <label className="flex items-center justify-between gap-2 text-xs text-[#707070]">
            Colore personalizzato
            <input type="color" defaultValue={lastColor}
                   className="w-8 h-6 border border-[#E5E5E0] cursor-pointer"
                   onChange={(e) => onPick(e.target.value)}
                   data-testid={testId ? `${testId}-color-custom` : undefined} />
          </label>
        </div>
      )}
    </div>
  );
}

function TBtn({ children, onClick, title, testId }) {
  return (
    <button type="button" onMouseDown={(e)=>e.preventDefault()} onClick={onClick} title={title}
            data-testid={testId}
            className="p-1.5 rounded-sm hover:bg-[#E5E5E0] text-[#1A1A1A]">
      {children}
    </button>
  );
}
function Sep() { return <div className="w-px h-4 bg-[#E5E5E0] mx-1" />; }

/* ------------------------- Smart paste sanitiser ------------------------- */

// Tags we keep (everything else is unwrapped and only its text/children stay).
const ALLOWED_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "S", "STRIKE",
  "P", "DIV", "BR", "SPAN",
  "UL", "OL", "LI",
  "H1", "H2", "H3", "H4",
  "BLOCKQUOTE",
]);
// Inline-style properties we keep (including only whitelisted families/sizes).
const ALLOWED_STYLES = new Set([
  "color",
  "font-weight",
  "font-style",
  "text-decoration",
  "font-family",
  "font-size",
]);

function sanitizePastedHtml(html) {
  // Strip Office/Google Docs noise BEFORE parsing.
  const cleaned = html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<o:p[\s\S]*?<\/o:p>/gi, "")
    .replace(/<o:[^>]*>/gi, "");

  const doc = new DOMParser().parseFromString(cleaned, "text/html");
  const root = doc.body;

  const walk = (node) => {
    // Snapshot children BEFORE we mutate the DOM (live NodeList footgun).
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          // Unwrap: replace the element with its children.
          walk(child);
          const fragment = doc.createDocumentFragment();
          while (child.firstChild) fragment.appendChild(child.firstChild);
          child.parentNode.replaceChild(fragment, child);
        } else {
          // Drop disallowed attributes; keep only filtered style.
          const tag = child.tagName;
          const stylesKept = [];
          if (child.hasAttribute("style")) {
            const raw = child.getAttribute("style");
            raw.split(";").forEach(part => {
              const idx = part.indexOf(":");
              if (idx < 0) return;
              const prop = part.slice(0, idx).trim().toLowerCase();
              const val = part.slice(idx + 1).trim();
              if (!val) return;
              if ((prop === "font-family" && FONTS.includes(val.replace(/["']/g, ""))) ||
                  (prop === "font-size" && SIZES.some((size) => val === `${size}px`)) ||
                  (ALLOWED_STYLES.has(prop) && prop !== "font-family" && prop !== "font-size")) {
                stylesKept.push(`${prop}: ${val}`);
              }
            });
          }
          // Wipe all attributes
          [...child.attributes].forEach(a => child.removeAttribute(a.name));
          // Re-apply curated style
          if (stylesKept.length) child.setAttribute("style", stylesKept.join("; "));
          // Recurse
          walk(child);
          // Normalise empty wrappers (e.g. <span>) — drop if no kids
          if ((tag === "SPAN" || tag === "DIV") && !child.firstChild) {
            child.parentNode.removeChild(child);
          }
        }
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode.removeChild(child);
      }
      // Text nodes are kept as-is.
    }
  };
  walk(root);

  // Trim leading/trailing whitespace + collapse multiple BR.
  return root.innerHTML
    .replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>")
    .trim();
}
